import React, { useEffect, useMemo, useState } from "react";

import type { SyncIssueEntry } from "../../../lib/offline/db";
import { deleteSyncIssue, listSyncIssues } from "../../../lib/offline/db";

type SyncIssueRecord = SyncIssueEntry & { id: number };

type SyncIssuesModalProps = {
  open: boolean;
  onClose: () => void;
  onChange?: (issues: SyncIssueRecord[]) => void;
};

const formatSummary = (issue: SyncIssueRecord) => {
  const metadataSummary = issue.metadata && typeof issue.metadata === "object" ? (issue.metadata as any).summary : null;
  if (metadataSummary && typeof metadataSummary === "object") {
    const values = Object.values(metadataSummary as Record<string, unknown>)
      .map((value) => (typeof value === "string" ? value : null))
      .filter(Boolean) as string[];
    if (values.length > 0) {
      return values.join(" • ");
    }
  }
  if (issue.body && typeof issue.body === "object" && "name" in (issue.body as Record<string, unknown>)) {
    const name = (issue.body as Record<string, unknown>).name;
    if (typeof name === "string" && name.trim().length) {
      return name;
    }
  }
  return `${issue.method} ${issue.endpoint}`;
};

const formatTimestamp = (value: number) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
};

export function SyncIssuesModal({ open, onClose, onChange }: SyncIssuesModalProps) {
  const [issues, setIssues] = useState<SyncIssueRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const result = await listSyncIssues();
      setIssues(result);
      onChange?.(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open]);

  const handleDismiss = async (id: number) => {
    await deleteSyncIssue(id);
    await refresh();
  };

  const issueCountLabel = useMemo(() => {
    if (issues.length === 0) return "No sync issues";
    if (issues.length === 1) return "1 record needs review";
    return `${issues.length} records need review`;
  }, [issues.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">Sync completed with issues</p>
            <p className="text-xs text-[var(--ulendo-text-muted)]">{issueCountLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-sm font-semibold text-[var(--ulendo-forest-dark)] hover:bg-[var(--ulendo-haze)]"
          >
            Close
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-5">
          {loading ? (
            <p className="text-sm text-[var(--ulendo-text-muted)]">Loading sync issues…</p>
          ) : issues.length === 0 ? (
            <p className="text-sm text-[var(--ulendo-text-muted)]">
              No conflicts were detected. The duplicate records were dropped during sync.
            </p>
          ) : (
            <div className="space-y-3">
              {issues.map((issue) => (
                <div key={issue.id} className="rounded-xl border border-[rgba(14,91,42,0.14)] bg-[var(--ulendo-haze)]/40 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--ulendo-forest-dark)]">{formatSummary(issue)}</p>
                      <p className="text-xs text-[var(--ulendo-text-muted)]">{formatTimestamp(issue.createdAt)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDismiss(issue.id)}
                      className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--ulendo-forest-dark)] hover:bg-[var(--ulendo-haze)]"
                    >
                      Dismiss
                    </button>
                  </div>
                  {issue.error ? (
                    <p className="mt-2 text-sm text-amber-700">{issue.error}</p>
                  ) : null}
                  <details className="mt-2 rounded-lg bg-white/80 p-3 text-sm text-[var(--ulendo-text-muted)]">
                    <summary className="cursor-pointer font-semibold text-[var(--ulendo-forest-dark)]">Review details</summary>
                    <pre className="mt-2 overflow-x-auto rounded bg-black/5 p-2 text-xs text-[var(--ulendo-forest-dark)]">
                      {JSON.stringify(issue.body ?? issue.metadata ?? {}, null, 2)}
                    </pre>
                    <p className="mt-2 text-xs text-[var(--ulendo-text-muted)]">
                      The server already has a matching managed lookup. Update the existing record if changes are needed.
                    </p>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
